// UI
import { TypographyVariant } from '@components/typography';

import cx from 'classnames';

export type ButtonColor = 'primary' | 'secondary' | 'gray' | 'black';
export type ButtonVariant = 'filled' | 'outlined' | 'text';
export type ButtonSize = 'sm' | 'md' | 'lg';

const fontVariants = {
  variantFontSizesAndLeading: {
    title1: 'text-4xl leading-loose',
    title3: 'text-2xl leading-4/3',
    title2: 'text-3xl leading-8/5',
    title4: 'text-xl leading-8/5',
    title5: 'text-lg leading-14/9',
    title6: 'text-base leading-normal',
    subtitle1: 'text-base leading-normal',
    subtitle2: 'text-sm leading-10/7',
    body1: 'text-base leading-normal',
    body2: 'text-sm leading-10/7',
    small: 'text-xs leading-5/3',
  },
  variantLetterSpacing: {
    title1: 'tracking-normal',
    title2: 'tracking-md',
    title3: 'tracking-lg',
    title4: 'tracking-lg',
    title5: 'tracking-wider',
    title6: 'tracking-wider',
    subtitle1: 'tracking-xl2',
    subtitle2: 'tracking-xl2',
    body1: 'tracking-wider',
    body2: 'tracking-wider',
    small: 'tracking-normal',
  },
  variantThickness: {
    bold: 'font-bold',
    title1: 'font-semibold',
    title2: 'font-semibold',
    title3: 'font-semibold',
    title4: 'font-semibold',
    title5: 'font-semibold',
    title6: 'font-semibold',
    subtitle1: 'font-semibold',
    subtitle2: 'font-semibold',
    body1: 'font-normal',
    body2: 'font-normal',
    small: 'font-normal',
  },
};

// TODO: Remove unecessary utils
export const getSizeStyleAsClassName = (
  size: ButtonSize,
  variant: ButtonVariant,
  disabled: boolean
): string => {
  const validPYs: { [key: number]: string } = {
    1: 'py-px ',
    2: 'py-0.5 ',
    3: 'py-[3px] ',
    4: 'py-1 ',
    5: 'py-[5px] ',
    6: 'py-1.5 ',
    7: 'py-[7px] ',
    8: 'py-2 ',
    9: 'py-[9px] ',
    10: 'py-2.5 ',
    11: 'py-[11px] ',
    12: 'py-3 ',
  };
  let typographyVariant = 'body1' as TypographyVariant;
  let paddingTopBottom = 12 as number;

  if (size === 'md') {
    typographyVariant = 'body2';
    paddingTopBottom = 10;
  } else if (size === 'sm') {
    typographyVariant = 'small';
    paddingTopBottom = 6;
  }
  if (variant === 'outlined' && !disabled) paddingTopBottom -= 2;
  if (disabled) paddingTopBottom -= 1;

  const fontSizeStyles =
    fontVariants.variantFontSizesAndLeading[typographyVariant];
  const letterSpacingStyles =
    fontVariants.variantLetterSpacing[typographyVariant];
  return cx(
    fontSizeStyles,
    letterSpacingStyles,
    validPYs[paddingTopBottom],
    'px-4 '
  );
};

const getFilledStyles = (color: ButtonColor): string => {
  return cx(
    'text-white',
    'hover:text-inherit',
    'active:text-inherit',
    'active:shadow-inner-0-2-2',
    {
      'bg-secondary-main': color === 'primary',
      'bg-primary-main': color === 'secondary',
      'bg-black': color === 'black',
      'bg-gray-main': color === 'gray',

      'hover:bg-primary-dark': color === 'primary' || color === 'secondary',
      'hover:text-secondary-main': color === 'primary',

      'hover:bg-gray-dark': color === 'black' || color === 'gray',

      'active:bg-gray-main': color !== 'primary',
      'active:bg-inherit': color === 'primary',

      'border-[1.5px] border-primary-main': color === 'primary',
      'text-primary-main': color === 'primary',
      'text-secondary-main': color === 'secondary',
    }
  );
};

const getOutlinedStyles = (color: ButtonColor): string => {
  return cx('bg-white', 'border-solid', {
    'bg-transparent': true,
    'text-primary-main': color === 'primary',
    'text-secondary-main': color === 'secondary',
    'text-black': color === 'black',
    'text-gray-main': color === 'gray',

    'border-[1.5px] border-primary-main': color === 'primary',
    'border-[1.5px] border-secondary-main': color === 'secondary',
    'border-[1.5px] border-black': color === 'black',
    'border-[1.5px] border-gray-main': color === 'gray',

    'hover:bg-gray-xlight hover:text-gray-main': color === 'gray',
    'hover:bg-inherit hover:text-primary-dark': color === 'primary',
    'hover:bg-inherit hover:text-secondary-dark': color === 'secondary',

    'active:bg-primary-xlight': color === 'primary',
    'active:bg-secondary-xlight': color === 'secondary',
    'active:bg-gray-xlight': color === 'gray',
    'active:bg-black': color === 'black',
  });
};

const getTextStyles = (color: ButtonColor): string => {
  return cx({
    'text-primary-main hover:text-primary-dark': color === 'primary',
    'text-secondary-main hover:text-secondary-dark': color === 'secondary',
    'text-gray-main hover:text-gray-dark': color === 'gray',
    'text-black hover:text-gray-dark': color === 'black',
  });
};

export const getVariantStyleAsClassName = (
  color: ButtonColor,
  variant: ButtonVariant
): string => {
  switch (variant) {
    case 'filled':
      return getFilledStyles(color);

    case 'outlined':
      return getOutlinedStyles(color);

    case 'text':
      return getTextStyles(color);

    default:
      throw new Error('Invalid variant');
  }
};
